import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1684478196406-47fe78877d54?fm=jpg&q=80&w=2400&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?fm=jpg&q=80&w=2400&auto=format&fit=crop",
  "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/2wan7ftr_image.png",
  "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/qifmu7od_IMG_9683.JPG",
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    HERO_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const goTo = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background slideshow */}
      <div className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={current}
            src={HERO_IMAGES[current]}
            alt="Karnival event production"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1.02 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{
              opacity: { duration: 1.6, ease: "easeInOut" },
              scale: { duration: 7, ease: "easeOut" },
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
      </div>
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-[#070707]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.06),_transparent_60%)]" />
      <div className="grain-overlay" />

      {/* Slide indicators */}
      <div
        className="absolute bottom-24 right-8 md:right-12 z-20 hidden md:flex flex-col gap-2"
        data-testid="hero-slide-indicators"
      >
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            data-testid={`hero-slide-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-px transition-all duration-500 ${
              i === current
                ? "w-12 bg-white"
                : "w-6 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-40 md:pt-44 pb-24 min-h-screen flex flex-col justify-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15 }}
          data-testid="hero-headline"
          className="font-serif text-white font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[92px] max-w-5xl"
        >
          Exceptional Entertainment.
          <br />
          <span className="italic text-white/90">Timeless Décor.</span>
          <br />
          <span className="text-[#FFFFFF]">Unforgettable</span> Moments.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          data-testid="hero-subheadline"
          className="mt-10 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          Ottawa&rsquo;s premier event production company — bringing world-class
          DJs, stunning décor, and jaw-dropping special effects to every
          celebration.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => goTo("/contact")}
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#FFFFFF] text-black font-medium hover:bg-[#F5F5F5] transition-all duration-300 hover:-translate-y-0.5"
          >
            Plan Your Event
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => goTo("#services")}
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-transparent border border-white/20 text-white hover:border-[#FFFFFF] hover:text-[#FFFFFF] transition-all duration-300"
          >
            <Play className="h-4 w-4" />
            View Our Services
          </button>
        </motion.div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-4xl"
          data-testid="hero-stats"
        >
          {[
            { n: "500+", l: "Events Produced" },
            { n: "10K+", l: "Guests Entertained" },
            { n: "5★", l: "Average Rating" },
            { n: "24/7", l: "Event Support" },
          ].map((s) => (
            <div key={s.l} className="border-l border-white/10 pl-4 md:pl-6">
              <div className="font-serif text-3xl md:text-4xl text-[#FFFFFF]">
                {s.n}
              </div>
              <div className="text-xs md:text-sm text-white/50 uppercase tracking-wider mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/40 text-xs tracking-widest">
        <span className="uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#FFFFFF] to-transparent" />
      </div>
    </section>
  );
}
